import React, { FC, useCallback, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { usePaymentContext } from 'features/payment';
import { IPaymentMethod } from 'features/payment/types';
import { Pressable, StyleSheet } from 'react-native';

import Currency from 'components/currency';
import { Box, Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getDesignToken } from 'styles/constants';

interface IPrepaidMethodBalance {
  method: IPaymentMethod;
  isDisabled?: boolean;
  isRedesign?: boolean;
  isMasked?: boolean;
}

const formatCardNumber = (cardNumber: string): string => {
  return (cardNumber.match(/.{1,4}/g) || []).join(' ');
};

const MaskDot = ({ color, size }: { color: string; size: number }) => (
  <Box style={[styles.maskDot, { backgroundColor: color, width: size, height: size }]} />
);

const MaskGroup = ({ color, size }: { color: string; size: number }) => (
  <Box style={styles.maskGroup}>
    {Array.from({ length: 4 }).map((_, index) => (
      <MaskDot key={index} color={color} size={size} />
    ))}
  </Box>
);

const MaskedNumber = ({ color, size }: { color: string; size: number }) => (
  <Box style={styles.maskNumberContainer}>
    {Array.from({ length: 4 }).map((_, index) => (
      <MaskGroup key={index} color={color} size={size} />
    ))}
  </Box>
);

const PrepaidMethodBalance: FC<IPrepaidMethodBalance> = ({
  method,
  isDisabled = false,
  isMasked = false,
}) => {
  const { getBalanceFromPaymentMethods } = usePaymentContext();
  const isDigitalGiftCardNumberExposedEnabled = useFlag(
    LaunchDarklyFlag.IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED
  );
  const [isNumberVisible, setIsNumberVisible] = useState(false);

  const toggleNumberVisibility = useCallback(() => {
    setIsNumberVisible(prev => !prev);
  }, []);

  const cardNumber = method.prepaid?.cardNumber;
  const textColor = isDisabled ? getDesignToken('text-disabled') : getDesignToken('text-default');
  const lastFour = cardNumber?.slice(-4);
  const optionText = isDigitalGiftCardNumberExposedEnabled
    ? `${t('giftCard')}${lastFour && ` ${t('endsIn').toLowerCase()} ${lastFour}`} -`
    : `${t('giftCard')}`;

  if (!isMasked || !isDigitalGiftCardNumberExposedEnabled) {
    return (
      <Box style={styles.balanceRow}>
        <Typography color={textColor} variant="bodyOne" fontFamily="body" fontSize={15}>
          {optionText}
        </Typography>
        <Currency
          amount={getBalanceFromPaymentMethods(method)}
          textProps={{
            style: { margin: 0 },
          }}
        />
      </Box>
    );
  }

  const CardNumberRow = cardNumber ? (
    <Box testID="gift-card-number-row" style={styles.maskNumberRow}>
      <Box testID="gift-card-number-value" style={styles.cardNumberValue}>
        {isNumberVisible ? (
          <DatadogSessionReplayMask>
            <Typography
              fontSize="xs"
              color={textColor}
              numberOfLines={1}
              testID="gift-card-number"
              style={styles.cardNumberText}
            >
              {formatCardNumber(cardNumber)}
            </Typography>
          </DatadogSessionReplayMask>
        ) : (
          <MaskedNumber color={textColor} size={5} />
        )}
      </Box>
      <Pressable
        onPress={toggleNumberVisibility}
        testID="toggle-gift-card-number"
        hitSlop={8}
        style={styles.visibilityToggle}
        accessibilityLabel={isNumberVisible ? 'Hide card number' : 'Show card number'}
        accessibilityRole="button"
      >
        <Icon
          variant={isNumberVisible ? 'visibility' : 'visibilityOff'}
          size="$6"
          color={getDesignToken('icon-default')}
        />
      </Pressable>
    </Box>
  ) : null;

  return (
    <Box style={styles.maskedContainer}>
      <Box style={styles.maskedBalanceRow}>
        <Typography
          color={isDisabled ? getDesignToken('text-disabled') : getDesignToken('text-default')}
        >
          {t('giftCard')}
        </Typography>
        <Currency
          amount={getBalanceFromPaymentMethods(method)}
          textProps={{
            style: { margin: 0 },
          }}
        />
      </Box>
      {CardNumberRow}
    </Box>
  );
};

const styles = StyleSheet.create({
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    flexShrink: 1,
    gap: 4,
  },
  maskedContainer: {
    flex: 1,
    flexDirection: 'column',
    minWidth: 0,
  },
  maskedBalanceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    gap: 8,
  },
  cardNumberValue: {
    flexShrink: 1,
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  cardNumberText: {
    flexShrink: 1,
  },
  maskDot: {
    width: 5,
    height: 5,
    borderRadius: 5,
  },
  maskGroup: {
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center',
  },
  maskNumberContainer: {
    flexDirection: 'row',
    flexShrink: 1,
    gap: 4,
    alignItems: 'center',
  },
  maskNumberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    gap: 4,
    marginTop: 2,
    maxWidth: '100%',
    minHeight: 24,
    overflow: 'hidden',
  },
  visibilityToggle: {
    alignItems: 'center',
    flexShrink: 0,
    height: 24,
    justifyContent: 'center',
    width: 28,
  },
});

export default PrepaidMethodBalance;
